// src/hooks/useSettings.js
import { useState, useCallback } from 'react';

export const useSettings = () => {
  const [settings, setSettings] = useState({
    discordWebhook: '',
    theme: 'dark',
    accentColor: 'gold',
    notifications: {
      priceAlerts: true,
      oiAlerts: true,
      systemAlerts: true
    },
    apiRateLimit: 'normal',
    dataRetention: '30'
  });

  const init = useCallback(() => {
    console.log('⚙️ Initializing Settings Manager...');
    loadSettings();
  }, []);

  const loadSettings = useCallback(() => {
    try {
      const stored = localStorage.getItem('binance_settings');
      if (stored) {
        const storedSettings = JSON.parse(stored);
        setSettings(prev => ({ ...prev, ...storedSettings }));
      }
      applySettings();
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  }, []);

  const saveSettings = useCallback((newSettings) => {
    try {
      const updatedSettings = { ...settings, ...newSettings };
      setSettings(updatedSettings);
      localStorage.setItem('binance_settings', JSON.stringify(updatedSettings));
      applySettings(updatedSettings);
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  }, [settings]);

  const applySettings = useCallback((settingsToApply = settings) => {
    document.documentElement.setAttribute('data-theme', settingsToApply.theme);
    document.documentElement.setAttribute('data-accent', settingsToApply.accentColor);
  }, [settings]);

  const updateSetting = useCallback((key, value) => {
    const newSettings = { ...settings };
    
    // Handle nested settings like notifications
    if (key.includes('.')) {
      const keys = key.split('.');
      let current = newSettings;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
    } else {
      newSettings[key] = value;
    }
    
    saveSettings(newSettings);
  }, [settings, saveSettings]);

  const resetToDefaults = useCallback(() => {
    const defaultSettings = {
      discordWebhook: '',
      theme: 'dark',
      accentColor: 'gold',
      notifications: {
        priceAlerts: true,
        oiAlerts: true,
        systemAlerts: true
      },
      apiRateLimit: 'normal',
      dataRetention: '30'
    };
    
    saveSettings(defaultSettings);
  }, [saveSettings]);

  return {
    settings,
    init,
    loadSettings,
    saveSettings,
    updateSetting,
    resetToDefaults
  };
};